import { DASH, money, money0, pct } from "../performance/columns";

/**
 * Columns for the per-product table, grouped the way the question gets asked:
 * what did we spend to bring people here, how many came and how far they got,
 * and what did it earn.
 *
 * Two conversion rates sit next to each other on purpose. Page CVR is sessions
 * landing here that bought *anything* — both sides from Shopify's sessions
 * data, so it ties out, and it is the honest measure of whether the traffic we
 * buy for this page converts. Product CVR is orders containing *this product*
 * over the same denominator, which answers a different question and does not
 * tie out the same way. Where they diverge, that gap is the finding.
 */
export const GROUPS = [
  {
    key: "product",
    label: "",
    columns: [{ key: "label", label: "Product", align: "left", locked: true, wrap: true }],
  },
  {
    key: "ads",
    label: "Ads",
    columns: [
      { key: "spend", label: "Spend", render: (r) => money0(r.spend) },
      {
        key: "costPerSession",
        label: "Cost / visit",
        title: "Ad spend divided by paid sessions landing on this page",
        render: (r) => money(r.costPerSession),
      },
      { key: "cpa", label: "CPA", render: (r) => money0(r.cpa) },
      {
        key: "mer",
        label: "MER",
        render: (r) => (r.mer === null || r.mer === undefined ? DASH : `${r.mer.toFixed(2)}×`),
      },
    ],
  },
  {
    key: "traffic",
    label: "Traffic",
    columns: [
      { key: "sessions", label: "Sessions", render: (r) => r.sessions.toLocaleString() },
      { key: "paidShare", label: "Paid", render: (r) => pct(r.paidShare, 0) },
      { key: "atcRate", label: "ATC", render: (r) => pct(r.atcRate) },
      {
        key: "pageCvr",
        label: "Page CVR",
        title: "Sessions landing here that ended in a purchase — of anything",
        render: (r) => pct(r.pageCvr),
      },
      {
        key: "productCvr",
        label: "Product CVR",
        title: "Orders containing this product, over sessions landing on its page",
        render: (r) => pct(r.productCvr),
      },
    ],
  },
  {
    key: "trading",
    label: "Trading",
    columns: [
      { key: "orders", label: "Orders", render: (r) => r.orders.toLocaleString() },
      { key: "units", label: "Units", render: (r) => r.units.toLocaleString() },
      { key: "aov", label: "AOV", render: (r) => money0(r.aov) },
      { key: "revenue", label: "Revenue", render: (r) => money0(r.revenue) },
    ],
  },
];

/** Columns the reader is allowed to hide — the product name always stays. */
export const HIDEABLE_COLUMN_KEYS = GROUPS.flatMap((g) =>
  g.columns.filter((c) => !c.locked).map((c) => c.key)
);

export function visibleGroups(hidden) {
  return GROUPS.map((g) => ({
    ...g,
    columns: g.columns.filter((c) => !hidden.has(c.key)),
  })).filter((g) => g.columns.length > 0);
}
